import { useState } from "react";
import { Target, Pencil, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface SalesGoalCardProps {
  currentSales: number;
  goalAmount: number;
  progressPercent: number;
  onSetGoal: (amount: number) => Promise<boolean>;
}

export function SalesGoalCard({
  currentSales,
  goalAmount,
  progressPercent,
  onSetGoal,
}: SalesGoalCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newGoal, setNewGoal] = useState(goalAmount.toString());
  const [isSaving, setIsSaving] = useState(false);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const amount = parseFloat(newGoal) || 0;
    const success = await onSetGoal(amount);
    if (success) {
      setIsEditing(false);
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    setNewGoal(goalAmount.toString());
    setIsEditing(false);
  };

  return (
    <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Target className="h-4 w-4" />
          Meta do Mês
        </CardTitle>
        {!isEditing && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {isEditing ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                type="number"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                placeholder="0.00"
                className="h-9"
              />
              <Button
                size="icon"
                className="h-9 w-9"
                onClick={handleSave}
                disabled={isSaving}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                className="h-9 w-9"
                onClick={handleCancel}
                disabled={isSaving}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : goalAmount > 0 ? (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold text-foreground">
                {progressPercent.toFixed(0)}%
              </span>
              <span className="text-xs text-muted-foreground">
                {formatCurrency(currentSales)} / {formatCurrency(goalAmount)}
              </span>
            </div>
            <Progress 
              value={Math.min(progressPercent, 100)} 
              className="h-2"
            />
            {progressPercent >= 100 && (
              <p className="text-xs text-success font-medium">
                🎉 Meta atingida!
              </p>
            )}
          </>
        ) : (
          <div className="text-center py-2">
            <p className="text-sm text-muted-foreground mb-2">
              Nenhuma meta definida
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Definir Meta
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
